import React from "react";
import { motion } from "framer-motion";
import { MessageSquareText, Phone, BellRing, TrendingUp, Users, Check, CheckCheck } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer } from "recharts";

const spark = [
  { v: 8 }, { v: 14 }, { v: 11 }, { v: 20 }, { v: 17 }, { v: 26 }, { v: 24 }, { v: 33 },
];

const float = (delay) => ({
  animate: { y: [0, -8, 0] },
  transition: { duration: 4, repeat: Infinity, ease: "easeInOut", delay },
});

/**
 * Abstract communication/automation dashboard — built entirely with JSX + Tailwind.
 */
export const HeroVisual = () => {
  return (
    <div className="relative mx-auto w-full max-w-[560px]">
      {/* Glow */}
      <div className="absolute -inset-6 rounded-[2rem] bg-[radial-gradient(circle_at_50%_30%,rgba(37,99,235,0.18),transparent_60%)] blur-xl" />

      {/* Device frame */}
      <div className="relative rounded-3xl border border-slate-200/70 bg-white shadow-[0_24px_80px_rgba(2,6,23,0.16)] overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
          <span className="ml-3 text-xs font-medium text-slate-400">E Telecom · Communication Hub</span>
          <span className="ml-auto rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-600">Live</span>
        </div>

        <div className="p-4 sm:p-5 grid grid-cols-3 gap-3">
          {/* Left rail */}
          <div className="col-span-1 flex flex-col gap-2">
            <RailItem icon={MessageSquareText} label="WhatsApp" active />
            <RailItem icon={BellRing} label="SMS" />
            <RailItem icon={Phone} label="Calls" />
            <RailItem icon={Users} label="Contacts" />
          </div>

          {/* Main */}
          <div className="col-span-2 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Kpi label="Messages" value="48.2k" trend="+12%" />
              <Kpi label="Delivery" value="98.6%" trend="+1.4%" />
            </div>
            <div className="rounded-xl border border-slate-100 p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-700">Engagement</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-600"><TrendingUp className="h-3 w-3" /> Trending</span>
              </div>
              <div className="h-16 mt-1">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={spark} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="heroGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="v" stroke="#2563EB" strokeWidth={2} fill="url(#heroGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Chat bubbles */}
            <div className="rounded-xl bg-slate-50 p-3 space-y-2">
              <div className="flex justify-start">
                <div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-white border border-slate-100 px-3 py-1.5 text-[11px] text-slate-700 shadow-sm">Hi! Is my order shipped?</div>
              </div>
              <div className="flex justify-end">
                <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-[#DCF8C6] px-3 py-1.5 text-[11px] text-slate-800 shadow-sm">
                  Yes! Order #4821 is out for delivery 🚚
                  <span className="ml-1 inline-flex align-middle"><CheckCheck className="h-3 w-3 text-blue-500" /></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating chips */}
      <motion.div {...float(0)} className="absolute -left-4 sm:-left-8 top-24 rounded-2xl bg-white border border-slate-200 shadow-lg px-3 py-2 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-green-50"><Phone className="h-3.5 w-3.5 text-green-600" /></span>
        <span className="text-[11px] font-semibold text-slate-700">Call connected</span>
      </motion.div>
      <motion.div {...float(1)} className="absolute -right-3 sm:-right-6 bottom-16 rounded-2xl bg-white border border-slate-200 shadow-lg px-3 py-2 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50"><Check className="h-3.5 w-3.5 text-blue-600" /></span>
        <span className="text-[11px] font-semibold text-slate-700">SMS delivered</span>
      </motion.div>
      <motion.div {...float(0.5)} className="absolute -right-2 top-6 rounded-2xl bg-[var(--et-navy)] shadow-lg px-3 py-2 flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-[var(--et-accent)]" />
        <span className="text-[11px] font-semibold text-white">Automation running</span>
      </motion.div>
    </div>
  );
};

const RailItem = ({ icon: Icon, label, active }) => (
  <div className={`flex items-center gap-2 rounded-lg px-2.5 py-2 ${active ? "bg-[rgba(37,99,235,0.08)]" : "hover:bg-slate-50"}`}>
    <Icon className={`h-4 w-4 ${active ? "text-[var(--et-primary)]" : "text-slate-400"}`} />
    <span className={`text-[11px] font-medium ${active ? "text-slate-900" : "text-slate-500"}`}>{label}</span>
  </div>
);

const Kpi = ({ label, value, trend }) => (
  <div className="rounded-xl border border-slate-100 p-3">
    <div className="text-[10px] font-medium text-slate-400">{label}</div>
    <div className="mt-0.5 font-display text-lg font-bold text-slate-900">{value}</div>
    <div className="text-[10px] font-semibold text-green-600">{trend}</div>
  </div>
);
